import { useCallback, useEffect, useMemo, useState } from "react";

import { PrimaryButton, TertiaryButton, Select } from "@components/v2";
import { ValueComparisonType } from "@north-beam/nb-client-side";
import { Modal } from "@shared/modal";

import { FiltersControl } from "./filters";
import { useOrdersQueryParams } from "./hooks/use-orders-filters";

export const DEFAULT_FILTERS = {
  orderValue: null,
  discountValue: null,
  shippingValue: null,
  taxValue: null,
  refundValue: null,
  numberOfOriginalItems: null,
  numberOfTouchpoints: null,
  attributed: null,
  orderTags: null,
  sourceName: null,
  customerTags: null,
  subscriptions: null,
  discountCodes: null,
  orderTypes: null,
  products: null,
  orderId: null,
  orderNumber: null,
  customerId: null,
};

const idToName = {
  orderId: "Order ID",
  orderNumber: "Order Number",
  orderValue: "Order Value",
  discountValue: "Discount Value",
  shippingValue: "Shipping Value",
  taxValue: "Tax Value",
  refundValue: "Refund Value",
  numberOfOriginalItems: "No. of original Items",
  numberOfTouchpoints: "No. of Touchpoints",
  attributed: "Attributed",
  orderTags: "Tags",
  sourceName: "Source Name",
  customerTags: "Customer Tags",
  subscriptions: "Subscription",
  discountCodes: "Discount Codes",
  orderTypes: "Order Type",
  products: "Products",
  customerId: "Customer ID",
} as const;

const idToType = {
  orderId: "value",
  orderNumber: "value",
  orderValue: "comparison",
  discountValue: "comparison",
  shippingValue: "comparison",
  taxValue: "comparison",
  refundValue: "comparison",
  numberOfOriginalItems: "comparison",
  numberOfTouchpoints: "comparison",
  attributed: "list",
  orderTags: "list",
  sourceName: "list",
  customerTags: "list",
  subscriptions: "list",
  discountCodes: "list",
  orderTypes: "list",
  products: "list",
  customerId: "value",
} as Record<ColumnKey, Filter["type"]>;

type ColumnKey = keyof typeof idToName;

export type FilterComparison = {
  type: "comparison";
  column: ColumnKey;
  comparisonOperator: ValueComparisonType;
  value: string;
};

export type FilterList = {
  type: "list";
  column: ColumnKey;
  value: string[];
};

export type FilterValue = {
  type: "value";
  column: ColumnKey;
  value: string;
};

export type Filter = FilterComparison | FilterList | FilterValue;

const isFilterComparison = (filter: Filter): filter is FilterComparison =>
  filter.type === "comparison";

const isFilterList = (filter: Filter): filter is FilterList =>
  filter.type === "list";

const isFilterValue = (filter: Filter): filter is FilterValue =>
  filter.type === "value";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FilterModal = ({ isOpen, onClose }: FilterModalProps) => {
  const [query, setQuery] = useOrdersQueryParams();

  const defaultState = useMemo(
    () =>
      Object.entries(query)
        .filter(([, value]) => value !== undefined)
        .map(([column, value]) => {
          const columnKey = column as ColumnKey;
          if (
            value &&
            typeof value === "object" &&
            "comparisonOperator" in value
          ) {
            return {
              type: "comparison",
              column: columnKey,
              comparisonOperator: value.comparisonOperator,
              value: String(value.value),
            } as FilterComparison;
          } else if (Array.isArray(value)) {
            return {
              type: "list",
              column: columnKey,
              value: value.filter((v) => v !== null).map(String),
            } as FilterList;
          } else {
            return {
              type: "value",
              column: columnKey,
              value: String(value),
            } as FilterValue;
          }
        }),
    [query],
  );

  const [localFilters, setLocalFilters] = useState<Filter[]>(defaultState);

  useEffect(() => {
    setLocalFilters(defaultState);
  }, [defaultState]);

  const handleRemoveFilter = (index: number) =>
    setLocalFilters((prevFilters) => prevFilters.filter((_, i) => i !== index));

  const handleAddFilter = () =>
    setLocalFilters((prevFilters) => [
      ...prevFilters,
      {
        type: "comparison",
        column: "" as ColumnKey,
        comparisonOperator: ValueComparisonType.greaterThan,
        value: "",
      },
    ]);

  const handleApplyFilters = () => {
    const filters = localFilters.reduce((acc, filter) => {
      if (isFilterComparison(filter)) {
        if (filter.value) {
          acc[filter.column] = {
            value: Number(filter.value),
            comparisonOperator: filter.comparisonOperator,
          };
        } else {
          acc[filter.column] = undefined;
        }
      } else if (isFilterList(filter)) {
        if (filter.value.length) {
          acc[filter.column] = filter.value;
        } else {
          acc[filter.column] = undefined;
        }
      } else if (isFilterValue(filter)) {
        if (filter.value) {
          acc[filter.column] = filter.value;
        } else {
          acc[filter.column] = undefined;
        }
      }
      return acc;
    }, {} as Record<ColumnKey, unknown>);

    const isEmpty = Object.values(filters).every(
      (value) =>
        value === undefined || (Array.isArray(value) && value.length === 0),
    );

    setQuery(
      isEmpty
        ? DEFAULT_FILTERS
        : ({ ...DEFAULT_FILTERS, ...filters } as typeof query),
    );
    onClose();
  };

  const getAvailableFilterOptions = useCallback(
    (column: ColumnKey) => {
      const currentlySelectedFilterIds = localFilters.map(
        ({ column }) => column,
      );

      return Object.entries(idToName)
        .map(([value, label]) => ({
          label,
          value: value as ColumnKey,
        }))
        .filter(
          (option) =>
            !currentlySelectedFilterIds.includes(option.value) ||
            option.value === column,
        );
    },
    [localFilters],
  );

  return (
    <Modal
      variant="v2"
      contentClassName="max-w-[720px] h-auto bg-v2-glass-inverse"
      isOpen={isOpen}
      onClose={onClose}
      titleContent="Filter"
    >
      <div className="bg-v2-primary border border-v2-tertiary text-v2-primary rounded p-6 h-full">
        <div className="font-semibold mb-6">Data Filters</div>
        <div className="flex flex-col gap-4 mx-auto">
          {localFilters.map((filter, index) => (
            <div key={index} className="flex gap-3 items-center">
              <Select
                className="w-48 [&_button]:!w-full"
                placeholder="Select Column"
                options={getAvailableFilterOptions(filter.column)}
                value={filter.column}
                hasSearch
                onChange={(value) =>
                  setLocalFilters((prevFilters) =>
                    prevFilters.map((f, i) =>
                      i === index && value
                        ? ({
                            ...f,
                            type: idToType[value],
                            column: value,
                          } as Filter)
                        : f,
                    ),
                  )
                }
              />
              {FiltersControl({
                filter,
                onChange: (updatedFilter) =>
                  setLocalFilters((prevFilters) =>
                    prevFilters.map((f, i) =>
                      i === index ? { ...f, ...updatedFilter } : f,
                    ),
                  ),
              })}
              <TertiaryButton
                onClick={() => handleRemoveFilter(index)}
                className="px-2"
              >
                <i className="fa-regular fa-trash uppercase text-mars"></i>
              </TertiaryButton>
            </div>
          ))}
          <TertiaryButton
            className="flex items-center text-primary font-semibold w-fit"
            onClick={handleAddFilter}
          >
            <i className="fa-regular fa-plus text-primary" />
            Filter
          </TertiaryButton>
        </div>
        <div className="flex justify-end items-center gap-2">
          <TertiaryButton onClick={onClose}>CANCEL</TertiaryButton>
          <PrimaryButton onClick={handleApplyFilters}>
            APPLY FILTERS
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};
